import React, { useState } from 'react';
import { 
  Search, Eye, Edit, Printer,
  ArrowUpDown, ArrowUp, ArrowDown, User,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Clock, Calendar, Pill, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { cn } from '@/lib/utils';
import { Prescription } from '@/types/prescription';
import { displayAge } from '@/lib/patientAge';

type SortField = 'patient' | 'date' | 'complaints' | 'medicines' | 'diagnosis';
type SortDirection = 'asc' | 'desc' | null;

interface PrescriptionDataTableProps {
  prescriptions: Prescription[];
  patients: any[];
  loading?: boolean;
  onView: (prescription: Prescription) => void;
  onEdit: (prescription: Prescription) => void;
  onPrint: (prescription: Prescription) => void;

  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  statusFilter?: string;
  onStatusChange?: (value: string) => void;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (order: 'asc' | 'desc') => void;
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
  hideSearchAndFilters?: boolean;
}

// Helper function to format relative time
const getRelativeTime = (date: string | Date) => {
  const now = new Date();
  const prescriptionDate = new Date(date);
  const diffInMinutes = Math.floor((now.getTime() - prescriptionDate.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 60) {
    return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) {
    return 'Yesterday';
  } else if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks}w ago`;
  } else {
    const months = Math.floor(diffInDays / 30);
    return `${months}mo ago`;
  }
};

// Helper function to check if date is today
const isToday = (date: string | Date) => {
  const today = new Date();
  const prescriptionDate = new Date(date);
  return today.toDateString() === prescriptionDate.toDateString();
};

// Medicine tooltip component
const MedicineTooltip = ({ medicines }: { medicines: any[] }) => {
  if (!medicines?.length) return null;
  
  return (
    <div className="space-y-2 max-w-80">
      <div className="font-medium text-sm mb-2">All Medications ({medicines.length})</div>
      {medicines.map((medicine, index) => (
        <div key={index} className="bg-medical-medicines/10 border border-medical-medicines/20 rounded-lg p-2">
          <div className="font-medium text-medical-medicines text-sm">{medicine.brand_name || medicine.product || 'Unknown Medicine'}</div>
        </div>
      ))}
    </div>
  );
};

export default function PrescriptionDataTable({
  prescriptions,
  patients,
  loading = false,
  onView,
  onEdit,
  onPrint,
  searchTerm = '',
  onSearchChange,
  statusFilter = 'all',
  onStatusChange,
  sortOrder = 'desc',
  onSortChange,
  currentPage,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  hideSearchAndFilters = false
}: PrescriptionDataTableProps) {

  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Get patient details helper
  const getPatientDetails = (patientId: string) => {
    return patients.find(p => p.id === patientId) || {
      name: 'Unknown Patient',
      age: 0,
      gender: 'Unknown',
      phone: 'N/A'
    };
  };

  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction or reset
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortField('date'); // Reset to default
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon for column
  const getSortIcon = (field: SortField) => {
    if (sortField !== field || sortDirection === null) {
      return <ArrowUpDown className="w-3 h-3 text-muted-foreground" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-primary" />
      : <ArrowDown className="w-3 h-3 text-primary" />;
  };

  // Sort prescriptions based on current sort settings
  const sortedPrescriptions = React.useMemo(() => {
    if (!sortField || sortDirection === null) {
      return prescriptions;
    }

    return [...prescriptions].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case 'patient':
          const patientA = getPatientDetails(a.patient_id);
          const patientB = getPatientDetails(b.patient_id);
          aVal = patientA.name.toLowerCase();
          bVal = patientB.name.toLowerCase();
          break;
        case 'date':
          aVal = new Date(a.date || a.created_at || '').getTime();
          bVal = new Date(b.date || b.created_at || '').getTime();
          break;
        case 'complaints':
          aVal = (a.complaints?.length || 0);
          bVal = (b.complaints?.length || 0);
          break;
        case 'medicines':
          aVal = (a.medicines?.length || 0);
          bVal = (b.medicines?.length || 0);
          break;
        case 'diagnosis':
          aVal = (a.diagnosis || '').toLowerCase();
          bVal = (b.diagnosis || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [prescriptions, sortField, sortDirection, patients]);

  // Handle row click to view prescription
  const handleRowClick = (prescription: Prescription, event: React.MouseEvent) => {
    // Don't trigger if clicking on buttons or checkboxes
    if ((event.target as HTMLElement).closest('button, input, a')) {
      return;
    }
    onView(prescription);
  };

  // Handle keyboard navigation
  const handleRowKeyDown = (prescription: Prescription, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onView(prescription);
    }
  };

  // Quick action handlers
  const handleQuickEdit = (prescription: Prescription, event: React.MouseEvent) => {
    event.stopPropagation();
    onEdit(prescription);
  };

  const handleQuickPrint = (prescription: Prescription, event: React.MouseEvent) => {
    event.stopPropagation();
    onPrint(prescription);
  };

  // Pagination
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/4"></div>
            <div className="h-10 bg-muted rounded"></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-muted rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card className="border-0 shadow-sm">
        <CardHeader className="border-b bg-muted/30 pb-3 hidden">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {totalItems} total prescriptions
            </div>
          </div>

          {/* Search and Filters - Conditionally render */}
          {!hideSearchAndFilters && (
            <div className="flex items-center gap-4 pt-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search prescriptions, patients, or phone numbers..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  className="pl-10 h-9"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={onStatusChange}>
                <SelectTrigger className="w-32 h-9">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              <Select value={itemsPerPage.toString()} onValueChange={(value) => onItemsPerPageChange(Number(value))}>
                <SelectTrigger className="w-28 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 rows</SelectItem>
                  <SelectItem value="25">25 rows</SelectItem>
                  <SelectItem value="50">50 rows</SelectItem>
                  <SelectItem value="100">100 rows</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardHeader>

        <CardContent className="p-0">
          {prescriptions.length === 0 ? (
            <div className="text-center py-12">
              <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No prescriptions found</h3>
              <p className="text-muted-foreground">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'Start by creating your first prescription'
                }
              </p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b bg-muted/20">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-64">
                        <button 
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                          onClick={() => handleSort('patient')}
                        >
                          Patient
                          {getSortIcon('patient')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-44">
                        <button 
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                          onClick={() => handleSort('date')}
                        >
                          Date & Time
                          {getSortIcon('date')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-48">
                        <button 
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                          onClick={() => handleSort('complaints')}
                        >
                          Chief Complaints
                          {getSortIcon('complaints')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-52">
                        <button 
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                          onClick={() => handleSort('medicines')}
                        >
                          Medications
                          {getSortIcon('medicines')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-40">
                        <button 
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                          onClick={() => handleSort('diagnosis')}
                        >
                          Diagnosis
                          {getSortIcon('diagnosis')}
                        </button>
                      </th>
                      <th className="w-24 px-4 py-3 text-center text-sm font-medium text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPrescriptions.map((prescription, index) => {
                      const patient = getPatientDetails(prescription.patient_id);
                      const prescriptionDate = new Date(prescription.date || prescription.created_at || '');
                      
                      return (
                        <tr 
                          key={prescription.id}
                          className={cn(
                            "group cursor-pointer transition-all duration-200 border rounded-lg p-4",
                            "hover:shadow-sm hover:border-primary/30",
                            "focus-within:bg-accent/50 focus-within:ring-1 focus-within:ring-primary/30"
                          )}
                          onClick={(event) => handleRowClick(prescription, event)}
                          onKeyDown={(event) => handleRowKeyDown(prescription, event)}
                          tabIndex={0}
                        >
                          {/* Patient */}
                          <td className="px-4 py-4 w-64">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-primary/10 to-primary/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:from-primary/20 group-hover:to-primary/30 transition-all duration-200">
                                <User className="w-5 h-5 text-primary" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                                  {patient?.name || prescription.patient_name || 'Unknown'}
                                </div>
                                <div className="text-sm text-muted-foreground truncate">
                                  {displayAge(patient?.age, patient?.date_of_birth)} • {patient?.gender} • {patient?.phone}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Enhanced Date & Time */}
                          <td className="px-4 py-4 w-44">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3 h-3 text-muted-foreground" />
                                <div className="font-medium text-foreground text-sm">
                                  {isToday(prescriptionDate) 
                                    ? 'Today' 
                                    : prescriptionDate.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })
                                  }
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="w-3 h-3 text-muted-foreground" />
                                <div className="text-xs text-muted-foreground">
                                  {prescriptionDate.toLocaleTimeString('en-US', {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  })} • {getRelativeTime(prescriptionDate)}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Complaints */}
                          <td className="px-4 py-4 w-48">
                            <div className="space-y-1">
                              {prescription.complaints?.length ? (
                                prescription.complaints.slice(0, 2).map((complaint, index) => (
                                  <Badge key={index} variant="secondary" className="text-xs bg-medical-complaints/10 text-medical-complaints hover:bg-medical-complaints/20 transition-colors border border-medical-complaints/20">
                                    {typeof complaint === 'string' ? complaint : complaint.complaint}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-xs text-muted-foreground italic">No complaints</span>
                              )}
                              {prescription.complaints?.length > 2 && (
                                <Badge variant="outline" className="text-xs text-muted-foreground">
                                  +{prescription.complaints.length - 2} more
                                </Badge>
                              )}
                            </div>
                          </td>

                          {/* Enhanced Medications with Tooltip */}
                          <td className="px-4 py-4 w-52">
                            <div className="space-y-1">
                              {prescription.medicines?.length ? (
                                                                  <>
                                    {prescription.medicines.slice(0, 2).map((medicine, index) => (
                                      <div key={index} className="bg-medical-medicines/10 border border-medical-medicines/20 rounded-md px-2 py-1 group-hover:bg-medical-medicines/20 transition-colors">
                                                                                  <div className="font-medium text-medical-medicines text-xs truncate">
                                          {medicine.product || 'Unknown Medicine'}
                                        </div>
                                      </div>
                                    ))}
                                  {prescription.medicines.length > 2 && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
                                          <Pill className="w-3 h-3" />
                                          +{prescription.medicines.length - 2} more
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent side="left" className="max-w-none">
                                        <MedicineTooltip medicines={prescription.medicines} />
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                <span className="text-xs text-muted-foreground italic">No medications</span>
                              )}
                            </div>
                          </td>

                          {/* Diagnosis */}
                          <td className="px-4 py-4 w-40">
                            <div className="text-xs">
                              {prescription.diagnosis ? (
                                <Badge className="bg-medical-diagnosis/10 text-medical-diagnosis hover:bg-medical-diagnosis/20 transition-colors border border-medical-diagnosis/20">
                                  {prescription.diagnosis}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground italic">No diagnosis</span>
                              )}
                            </div>
                          </td>

                          {/* Enhanced Actions */}
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:scale-105">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary transition-all duration-200"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      onView(prescription);
                                    }}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>View Details</TooltipContent>
                              </Tooltip>
                              
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-8 w-8 p-0 hover:bg-success/10 hover:text-success transition-all duration-200"
                                    onClick={(event) => handleQuickEdit(prescription, event)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Edit Prescription</TooltipContent>
                              </Tooltip>
                              
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-8 w-8 p-0 hover:bg-warning/10 hover:text-warning transition-all duration-200"
                                    onClick={(event) => handleQuickPrint(prescription, event)}
                                  >
                                    <Printer className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Print Prescription</TooltipContent>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Enhanced Pagination */}
              {totalPages > 1 && (
                <div className="border-t bg-gradient-to-r from-muted/20 to-muted/10 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      Showing <span className="font-medium text-foreground">{startItem}</span> to{' '}
                      <span className="font-medium text-foreground">{endItem}</span> of{' '}
                      <span className="font-medium text-foreground">{totalItems}</span> prescriptions
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPageChange(1)}
                        disabled={currentPage === 1}
                        className="h-9 w-9 p-0 hover:bg-primary/10 transition-colors"
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="h-9 w-9 p-0 hover:bg-primary/10 transition-colors"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>

                      <div className="flex items-center gap-1">
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                          const pageNum = currentPage - 2 + i;
                          if (pageNum < 1 || pageNum > totalPages) return null;
                          
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => onPageChange(pageNum)}
                              className="h-9 w-9 p-0 transition-colors"
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="h-9 w-9 p-0 hover:bg-primary/10 transition-colors"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className="h-9 w-9 p-0 hover:bg-primary/10 transition-colors"
                      >
                        <ChevronsRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
} 